import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import PublicBooking from './PublicBooking';
import { CalendarX, Loader2 } from 'lucide-react';

const PublicBookingTemporal = () => {
    const { uuid } = useParams();
    const location = useLocation();
    
    // El token viene después del ? ej. ?meet5-SD90
    const token = location.search.substring(1);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isValid, setIsValid] = useState(false);
    const [linkData, setLinkData] = useState(null);

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setError("Enlace temporal inválido. No se encontró el código.");
                setLoading(false);
                return;
            }

            try {
                const res = await fetch(`/api/temporal-links/validate?token=${token}`);
                const data = await res.json();

                if (res.ok && data.success) {
                    setIsValid(true);
                    setLinkData(data.data.link);
                } else {
                    setError(data.error || "Este enlace ha caducado o ya fue utilizado.");
                }
            } catch (err) {
                setError("Hubo un error de conexión al validar el enlace.");
            } finally {
                setLoading(false);
            }
        };

        validateToken();
    }, [token]);

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
                <Loader2 size={40} color="#3b82f6" style={{ animation: "spin 1s linear infinite", marginBottom: "16px" }} />
                <p style={{ color: '#4b5563', fontFamily: 'sans-serif' }}>Validando enlace...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', padding: '20px', textAlign: 'center' }}>
                <CalendarX size={64} color="#ef4444" style={{ marginBottom: "24px" }} />
                <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Enlace Caducado</h1>
                <p style={{ color: '#4b5563', maxWidth: '400px', lineHeight: '1.5' }}>{error}</p>
            </div>
        );
    }

    if (isValid) {
        // Pasamos el token a PublicBooking para que lo mande en el POST /book
        return (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <PublicBooking temporalToken={token} />
                {linkData && <CountdownTimer expiresAt={linkData.expires_at} />}
            </div>
        );
    }

    return null;
};

const CountdownTimer = ({ expiresAt }) => {
    const [timeLeft, setTimeLeft] = useState('');

    useEffect(() => {
        const updateTimer = () => {
            const now = new Date().getTime();
            const expiration = new Date(expiresAt).getTime();
            const diff = expiration - now;

            if (diff <= 0) {
                setTimeLeft('Expirado');
                return;
            }

            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            let timeString = '';
            if (hours > 0) timeString += `${hours}h `;
            timeString += `${minutes}m ${seconds}s`;

            setTimeLeft(timeString);
        };

        updateTimer();
        const intervalId = setInterval(updateTimer, 1000);

        return () => clearInterval(intervalId);
    }, [expiresAt]);

    return (
        <div style={{
            position: 'fixed',
            bottom: '5px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#1f2937',
            color: '#f3f4f6',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '500',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 9,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        }}>
            <span style={{ color: '#9ca3af' }}>Expira en:</span>
            <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>{timeLeft}</span>
        </div>
    );
};

export default PublicBookingTemporal;
